/**
 * useFaceSwapLiveKit — Zero-Inbound LiveKit WebRTC face swap and control plane.
 *
 * Transports:
 *   - Video: Native WebRTC tracks (Webcam 720p @ 30fps -> Agent -> Processed Track)
 *   - Control: WebRTC DataChannel (reliable JSON):
 *       * Target face photo reference upload (topic: 'face_reference')
 *       * Real-time engine toggles, profile activation (topic: 'control')
 *       * Reactive engine status push from GPU worker (topic: 'status')
 *
 * Eliminates HTTP polling and third-party tunnels entirely.
 */

import { useCallback, useRef, useState, useEffect } from "react";
import {
  Room,
  RoomEvent,
  Track,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  LocalTrackPublication,
  LocalVideoTrack,
  createLocalVideoTrack,
} from "livekit-client";
import { API_BASE } from "@/lib/api";
import { isLiteEdition } from "@/lib/config";

const lg = (...a: unknown[]) => console.log("[FaceSwap/LK]", ...a);
const le = (...a: unknown[]) => console.error("[FaceSwap/LK]", ...a);

export type SwapStatus = "idle" | "connected" | "connecting" | "waiting" | "active" | "error";

export interface FaceProfile {
  profile_id: string;
  name: string;
  is_active: boolean;
}

export interface HardwareInfo {
  gpu_name: string;
  vram_gb?: number;
  cpu_count?: number;
  platform?: string;
  ram_gb?: number;
  cuda_version?: string;
  is_cloud?: boolean;
}

export interface EngineStatus {
  type?: string;
  engine_ready: boolean;
  engine_type: string | null;
  active_profile: string | null;
  profiles: FaceProfile[];
  enhancer?: string;
  enhance_face?: boolean;
  gfpgan_quality?: string;
  slot_id?: number;
  gpu_name?: string;
  hardware?: HardwareInfo;
}

export function useFaceSwapLiveKit() {
  const roomRef = useRef<Room | null>(null);
  const localVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const localPubRef = useRef<LocalTrackPublication | null>(null);
  const processedVideoRef = useRef<HTMLVideoElement | null>(null);
  const fpsCountRef = useRef(0);
  const fpsStartRef = useRef(Date.now());

  const [status, setStatus] = useState<SwapStatus>("idle");
  const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
  const [fps, setFps] = useState(0);
  const [frameCount, setFrameCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [engineStatus, setEngineStatus] = useState<EngineStatus | null>(null);

  // ── Send JSON control message over DataChannel ──────────────────────────────
  const sendControlMessage = useCallback(async (action: string, payload: Record<string, unknown> = {}) => {
    if (roomRef.current && roomRef.current.localParticipant) {
      try {
        const msg = JSON.stringify({ action, payload });
        const encoder = new TextEncoder();
        await roomRef.current.localParticipant.publishData(encoder.encode(msg), {
          topic: "control",
          reliable: true,
        });
        return true;
      } catch (e) {
        le("Failed to send control message:", e);
      }
    }
    return false;
  }, []);

  // ── Upload reference image via DataChannel (with chunking for 65,535-byte MTU) ──
  const uploadReference = useCallback(async (data: Uint8Array, name: string = "uploaded") => {
    if (roomRef.current && roomRef.current.localParticipant) {
      lg(`Uploading reference "${name}" via DataChannel (${data.byteLength} bytes)...`);

      if (data.byteLength <= 60000) {
        await roomRef.current.localParticipant.publishData(data as any, {
          topic: "face_reference",
          reliable: true,
        });
        return true;
      }

      // Chunk into safe 30KB blocks to honor 65,535-byte WebRTC MTU cap
      const CHUNK_SIZE = 30000;
      const totalChunks = Math.ceil(data.byteLength / CHUNK_SIZE);
      const uploadId = `ref_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, data.byteLength);
        const slice = data.slice(start, end);

        let binary = "";
        for (let j = 0; j < slice.byteLength; j++) {
          binary += String.fromCharCode(slice[j]);
        }
        const base64Chunk = btoa(binary);

        const packet = JSON.stringify({
          type: "chunk",
          id: uploadId,
          name,
          index: i,
          total: totalChunks,
          data: base64Chunk,
        });

        const packetBytes = new TextEncoder().encode(packet);
        await roomRef.current.localParticipant.publishData(packetBytes as any, {
          topic: "face_reference",
          reliable: true,
        });
      }

      lg(`Successfully streamed ${totalChunks} chunks for upload ${uploadId} (${name})`);
      return true;
    }
    return false;
  }, []);

  // ── Clear session via DataChannel ──────────────────────────────────────────
  const clearSession = useCallback(async () => {
    lg("Clearing session via DataChannel...");
    try {
      const { clearFaceVault } = await import("@/lib/faceVault");
      await clearFaceVault();
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem("streampersona_active_profile");
      localStorage.removeItem("streampersona_profiles");
    } catch {
      /* ignore */
    }
    return sendControlMessage("clear_session");
  }, [sendControlMessage]);

  // ── Connect to room (control plane & status listener) ──────────────────────
  const connectRoom = useCallback(async (rName?: string, token?: string, lkUrl?: string) => {
    if (!token || !lkUrl) {
      return null;
    }
    if (roomRef.current && roomRef.current.state === "connected") {
      return roomRef.current;
    }

    try {
      lg(`[FaceSwap/LK] Auto-connecting control plane to room: ${rName} at ${lkUrl}...`);
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });
      roomRef.current = room;

      room.on(
        RoomEvent.TrackSubscribed,
        (
          track: RemoteTrack,
          _pub: RemoteTrackPublication,
          participant: RemoteParticipant,
        ) => {
          if (participant.identity === "face-agent" && track.kind === Track.Kind.Video) {
            lg("Subscribed to agent's processed video track!");
            setStatus("active");
            fpsCountRef.current = 0;
            fpsStartRef.current = Date.now();

            const videoEl = processedVideoRef.current;
            if (videoEl) {
              track.attach(videoEl);
              lg("Attached processed video to output element");
            }
            if (track.mediaStreamTrack) {
              setProcessedStream(new MediaStream([track.mediaStreamTrack]));
            }

            // FPS counter
            if (videoEl && "requestVideoFrameCallback" in videoEl) {
              const countFrame = () => {
                fpsCountRef.current++;
                setFrameCount((c) => c + 1);
                const elapsed = (Date.now() - fpsStartRef.current) / 1000;
                if (elapsed >= 2) {
                  const f = Math.round(fpsCountRef.current / elapsed);
                  setFps(f);
                  fpsCountRef.current = 0;
                  fpsStartRef.current = Date.now();
                }
                if (roomRef.current) {
                  (videoEl as any).requestVideoFrameCallback(countFrame);
                }
              };
              (videoEl as any).requestVideoFrameCallback(countFrame);
            }
          }
        }
      );

      // Reactive Status Listener: Receive engine status & dynamic GPU discovery pushes
      room.on(RoomEvent.DataReceived, (payload: Uint8Array, _participant, _kind, topic) => {
        if (topic === "status") {
          try {
            const text = new TextDecoder().decode(payload);
            const statusData = JSON.parse(text) as EngineStatus;
            
            // Populate dynamic hardware specs if provided by worker
            if (!statusData.hardware && statusData.gpu_name) {
              statusData.hardware = {
                gpu_name: statusData.gpu_name,
                vram_gb: statusData.gpu_name.includes("5090") ? 32 : 16,
                is_cloud: true,
                platform: "Remote GPU Cloud Node (RTX 5090 Blackwell)"
              };
            }
            lg("[FaceSwap/LK] Received status update:", statusData);
            setEngineStatus(statusData);
          } catch (e) {
            le("Failed to parse reactive status packet:", e);
          }
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach();
        if (track.kind === Track.Kind.Video) {
          setProcessedStream(null);
        }
        lg("Track detached");
      });

      room.on(RoomEvent.Disconnected, () => {
        lg("Disconnected from room");
        setStatus("idle");
        setRoomName(null);
        localVideoTrackRef.current = null;
        localPubRef.current = null;
      });

      await room.connect(lkUrl, token);
      setRoomName(rName || null);
      setStatus("connected");
      lg("Connected to room successfully. Requesting status...");

      setTimeout(() => {
        sendControlMessage("get_status");
      }, 300);

      return room;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      le("Control plane connect failed:", msg);
      setError(msg);
      setStatus("error");
      return null;
    }
  }, [sendControlMessage]);

  // ── Start video swap (Publish webcam to connected room) ────────────────────
  const startSwap = useCallback(async (customRoomName?: string, customToken?: string, customLkUrl?: string, deviceId?: string) => {
    setStatus("connecting");
    setError(null);
    setFrameCount(0);
    setFps(0);

    try {
      let room = roomRef.current;
      if (!room || room.state !== "connected") {
        room = await connectRoom(customRoomName, customToken, customLkUrl);
        if (!room) {
          throw new Error("Unable to establish LiveKit connection to cloud node");
        }
      }

      setStatus("waiting");
      lg("Publishing webcam...");

      // Stop previous local track if present
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
      }

      const videoTrack = await createLocalVideoTrack({
        resolution: { width: 640, height: 480, frameRate: 30 },
        deviceId: deviceId || undefined,
        facingMode: deviceId ? undefined : "user",
      });
      localVideoTrackRef.current = videoTrack;

      const pub = await room.localParticipant.publishTrack(videoTrack);
      localPubRef.current = pub;
      lg("Webcam published — waiting for agent to process...");

      sendControlMessage("get_status");

    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      le("Start swap failed:", msg);
      setError(msg);
      setStatus("error");
    }
  }, [connectRoom, sendControlMessage]);

  // ── Stop swap (Unpublish track while keeping room connected) ──────────────
  const stopSwap = useCallback(async () => {
    lg("Stopping swap...");

    if (localPubRef.current && roomRef.current?.localParticipant) {
      try {
        await roomRef.current.localParticipant.unpublishTrack(localPubRef.current.track!);
      } catch (e) {
        /* best effort */
      }
      localPubRef.current = null;
    }

    if (localVideoTrackRef.current) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current = null;
    }

    if (!isLiteEdition()) {
      try {
        await fetch(`${API_BASE()}/api/face/swap-room/end`, { method: "POST" });
      } catch {
        /* best effort */
      }
    }

    setStatus(roomRef.current?.state === "connected" ? "connected" : "idle");
    setFps(0);
    setProcessedStream(null);
    lg("Swap stopped (control plane remains ready)");
  }, []);

  return {
    connectRoom,
    startSwap,
    stopSwap,
    clearSession,
    uploadReference,
    sendControlMessage,
    engineStatus,
    status,
    fps,
    frameCount,
    error,
    roomName,
    processedVideoRef,
    processedStream,
  };
}
